/** The Analytics view's data: San Francisco metrics by link, hexagon, city TAZ and
 *  census tract, for each hour of a weekday and each workshop scenario built, by
 *  scripts/build_analytics_sf.js into /data/analytics/<cbsa>/: shared geometry at the
 *  top, one folder of values per scenario, and manifest.json saying which scenarios
 *  exist. Files load on demand and once per page.
 *
 *  Sources: PPS4SUMO (traffic and emissions per link), AERMOD (PM2.5 concentration),
 *  BenMAP (health outcomes per tract from San Francisco's own run; a scenario without
 *  one would be illustrative, inferred from the Boston and NY results, see the build
 *  script and manifest.json health_source). */

import { cellToBoundary } from 'h3-js';
import { SHOW_OD_PRICING_TABS } from '../../config/features';

// 'taz' (the 31 city hexagons of the old San Francisco TAZ grid) is still in the data and
// in METRICS levels but not offered: H3 hexagons over the Bay Area carry the same metrics.
export const LEVELS = [
  { id: 'link', label: 'Link' },
  { id: 'hex', label: 'Hexagon' },
  { id: 'tract', label: 'Tract' },
];

/** `levels`: where the metric exists. `sum`: a citywide hour adds it up (else a mean).
 *  `better`: which way is good, for the colour ramp. `hourly`: false for an annual figure.
 *  `pricingMap`: drawn by the pricing map (V/C, Baseline / Scenario / Difference), not here;
 *  that map is the Congestion Pricing view the SF demo hides (SHOW_OD_PRICING_TABS). */
const ALL_METRICS = [
  { id: 'vc', group: 'Congestion', label: 'V/C, by pricing scenario', source: 'SUMO scenario runs', pricingMap: true },
  { id: 'speed', group: 'Traffic', label: 'Speed', unit: 'mph', levels: ['link', 'hex', 'taz'], better: 'higher', source: 'PPS4SUMO', digits: 1 },
  { id: 'flow', group: 'Traffic', label: 'Flow', unit: 'veh/h', levels: ['link', 'hex', 'taz'], source: 'PPS4SUMO', digits: 0 },
  { id: 'density', group: 'Traffic', label: 'Density', unit: 'veh/mi/lane', levels: ['link', 'hex', 'taz'], source: 'PPS4SUMO', digits: 1 },
  { id: 'vmt', group: 'Traffic', label: 'VMT', unit: 'veh-mi', levels: ['link', 'hex', 'taz'], sum: true, source: 'PPS4SUMO', digits: 0 },
  { id: 'vht', group: 'Traffic', label: 'VHT', unit: 'veh-h', levels: ['link', 'hex', 'taz'], sum: true, source: 'PPS4SUMO', digits: 1 },
  { id: 'pm25', group: 'Emissions', label: 'PM2.5 emitted', unit: 'g', levels: ['link', 'hex', 'taz'], sum: true, source: 'PPS4SUMO', digits: 1 },
  { id: 'nox', group: 'Emissions', label: 'NOx emitted', unit: 'g', levels: ['link', 'hex', 'taz'], sum: true, source: 'PPS4SUMO', digits: 0 },
  { id: 'pm25_road', group: 'Air quality', label: 'PM2.5 from road traffic', unit: 'µg/m³', levels: ['hex', 'taz', 'tract'], source: 'AERMOD', digits: 3 },
  { id: 'pm25_conc', group: 'Air quality', label: 'PM2.5 concentration, total', unit: 'µg/m³', levels: ['hex', 'taz', 'tract'], source: 'AERMOD', digits: 2 },
  { id: 'deaths', group: 'Health & equity', label: 'Premature deaths', unit: 'per year', levels: ['tract'], sum: true, hourly: false, source: 'BenMAP', digits: 3 },
  { id: 'deaths_per_100k', group: 'Health & equity', label: 'Premature deaths per 100k residents', unit: 'per year', levels: ['tract'], hourly: false, source: 'BenMAP', digits: 2 },
  { id: 'er_asthma', group: 'Health & equity', label: 'Asthma emergency visits', unit: 'per year', levels: ['tract'], sum: true, hourly: false, source: 'BenMAP', digits: 3 },
  { id: 'asthma_ex', group: 'Health & equity', label: 'Asthma attacks (cough days)', unit: 'per year', levels: ['tract'], sum: true, hourly: false, source: 'BenMAP', digits: 1 },
  { id: 'ha_resp', group: 'Health & equity', label: 'Hospital admissions, respiratory', unit: 'per year', levels: ['tract'], sum: true, hourly: false, source: 'BenMAP', digits: 3 },
  { id: 'ha_cardio', group: 'Health & equity', label: 'Hospital admissions, cardiovascular', unit: 'per year', levels: ['tract'], sum: true, hourly: false, source: 'BenMAP', digits: 3 },
  { id: 'ami', group: 'Health & equity', label: 'Non-fatal heart attacks', unit: 'per year', levels: ['tract'], sum: true, hourly: false, source: 'BenMAP', digits: 3 },
];
export const METRICS = ALL_METRICS.filter((m) => SHOW_OD_PRICING_TABS || !m.pricingMap);
export const metricOf = (id) => METRICS.find((m) => m.id === id);
export const HOURS = 24;

const KM_PER_MI = 1.609344;
/** A link metric from the per-edge fields /api/traffic/links returns for the roads in view
 *  (vehCount, speedKmh, vmtMi, lanes, lengthM), with PPSforSUMO's formulas: the same values
 *  scripts/build_analytics_*.js write to links_<metric>.json. A link with no vehicle in the
 *  hour has no value, as in those files. */
export function linkValue(p, metricId) {
  const n = p.vehCount;
  if (!(n >= 1)) return null;
  const kmh = Number(p.speedKmh);
  const mph = kmh / KM_PER_MI;
  const km = Number(p.lengthM) / 1000;
  switch (metricId) {
    case 'flow': return n;
    case 'speed': return mph;
    case 'density': return kmh > 0 ? (n / kmh / (p.lanes || 1)) * KM_PER_MI : null;
    case 'vmt': return Number(p.vmtMi);
    case 'vht': return mph > 0 ? Number(p.vmtMi) / mph : null;
    case 'pm25': return n * km * 0.006;
    case 'nox': return n * km * 0.09;
    default: return null;
  }
}

// ---- loading -----------------------------------------------------------------

const cache = new Map();
function getJson(url) {
  let p = cache.get(url);
  if (!p) {
    p = fetch(url).then((res) => {
      if (!res.ok) throw new Error(`${url.split('/').pop()} ${res.status}`);
      return res.json();
    });
    p.catch(() => cache.delete(url));
    cache.set(url, p);
  }
  return p;
}

/** One scenario's features at one level with one metric for every hour:
 *  { features: [{ id, name, path | polygon | geometry, props }], values: (i, h) -> number|null,
 *    healthSource }. */
export async function getLayer(cbsa, scenario, level, metricId) {
  const base = `/data/analytics/${cbsa}`;
  const own = `${base}/${scenario}`;
  if (level === 'link') {
    const [links, m] = await Promise.all([getJson(`${base}/links.json`), getJson(`${own}/links_${metricId}.json`)]);
    const features = links.ids.map((id, i) => ({
      id,
      // An OSM way with no name (mostly ramps): its road class, e.g. "Unnamed motorway ramp".
      name: links.names[i] || `Unnamed ${(links.types[i] || 'road').replace('_link', ' ramp').replace('_', ' ')}`,
      kind: links.types[i],
      path: links.paths[i],
    }));
    return { features, values: (i, h) => m.values[i * HOURS + h] };
  }
  if (level === 'hex' || level === 'taz') {
    const [doc, shapes, hexNames] = await Promise.all([
      getJson(`${own}/${level}.json`),
      level === 'taz' ? getJson(`${base}/taz_shapes.json`) : null,
      // "Near <road>, <county>" per cell (scripts/build_analytics_hex_names.py); optional.
      level === 'hex' ? getJson(`${base}/hex_names.json`).catch(() => null) : null,
    ]);
    const v = doc.metrics[metricId];
    if (!v) throw new Error(`no ${metricId} at this level`);
    const polygonOf = shapes ? new Map(shapes.ids.map((id, i) => [id, shapes.polygons[i]])) : null;
    const nameOf = hexNames ? new Map(hexNames.ids.map((id, i) => [id, hexNames.names[i]])) : null;
    const features = doc.ids.map((id) => ({
      id,
      name: level === 'hex' ? (nameOf?.get(id) || 'Bay Area') : `City hexagon ${id}`,
      polygon: level === 'hex' ? cellToBoundary(id).map(([lat, lng]) => [lng, lat]) : polygonOf.get(id),
    }));
    return { features, values: (i, h) => v[i * HOURS + h] };
  }
  const [shared, doc] = await Promise.all([getJson(`${base}/tracts.json`), getJson(`${own}/tracts.json`)]);
  const at = new Map(doc.ids.map((id, i) => [id, i]));
  const features = shared.features.map((f) => ({
    id: f.properties.GEOID,
    name: f.properties.name,
    geometry: f.geometry,
    props: f.properties,
    k: at.get(f.properties.GEOID),
  }));
  const hourly = doc.hourly[metricId];
  const annual = doc.annual[metricId];
  if (!hourly && !annual) throw new Error(`no ${metricId} by tract`);
  return {
    features,
    healthSource: doc.health_source,
    // A tract's yearly figures whatever the metric shown (the panel's road PM2.5).
    annualOf: (i, key) => (features[i].k == null ? null : doc.annual[key]?.[features[i].k] ?? null),
    values: hourly
      ? (i, h) => (features[i].k == null ? null : hourly[features[i].k * HOURS + h])
      : (i) => (features[i].k == null ? null : annual[features[i].k]),
  };
}

export const getManifest = (cbsa) => getJson(`/data/analytics/${cbsa}/manifest.json`);

/** Every built scenario's citywide figures per hour (totals.json), by scenario id: the
 *  headline cards' real numbers. Empty when nothing is built. */
export async function getTotals(cbsa) {
  const manifest = await getManifest(cbsa);
  const built = manifest.scenarios.filter((s) => s.available);
  const docs = await Promise.all(built.map((s) => getJson(`/data/analytics/${cbsa}/${s.id}/totals.json`).catch(() => null)));
  return Object.fromEntries(built.map((s, i) => [s.id, docs[i]]).filter(([, d]) => d));
}

// ---- colour --------------------------------------------------------------------

/** Traffic-light ramp, low to high: green (less) through yellow to red (more), the
 *  reading people bring to a traffic map. Reversed where higher is better (speed: green
 *  is fast). The green end is darker than the red, and yellow sits in the middle, so the
 *  steps also differ in lightness for readers who cannot tell red from green. */
const RAMP = [
  [26, 152, 80],   // #1a9850
  [145, 207, 96],  // #91cf60
  [254, 224, 139], // #fee08b
  [252, 141, 89],  // #fc8d59
  [215, 48, 39],   // #d73027
];

/** Quantile bins over every hour's values, so the colours mean the same at every hour:
 *  { colorOf(v), rows: [{ label, color }] }. */
export function scaleFor(layer, metric) {
  const hours = metric.hourly === false ? 1 : HOURS;
  // A typed array sorts numerically without a comparator: 1.6M link values in ~50 ms
  // instead of ~450 ms with Array.sort((a, b) => a - b); the breaks are the same.
  const all = new Float64Array(layer.features.length * hours);
  let n = 0;
  for (let i = 0; i < layer.features.length; i++) {
    for (let h = 0; h < hours; h++) {
      const v = layer.values(i, h);
      if (v != null && Number.isFinite(v)) all[n++] = v;
    }
  }
  const vals = all.subarray(0, n).sort();
  const ramp = metric.better === 'higher' ? [...RAMP].reverse() : RAMP;
  if (!vals.length) return { colorOf: () => ramp[0], rows: [] };
  const breaks = [];
  for (let k = 1; k < ramp.length; k++) {
    const v = vals[Math.floor((k / ramp.length) * (vals.length - 1))];
    if (!breaks.length || v > breaks[breaks.length - 1]) breaks.push(v);
  }
  const colorAt = (i) => ramp[Math.round((i * (ramp.length - 1)) / Math.max(1, breaks.length))];
  const colorOf = (v) => {
    let i = 0;
    while (i < breaks.length && v > breaks[i]) i++;
    return colorAt(i);
  };
  const fmt = (v) => fmtValue(v, metric);
  const edges = [vals[0], ...breaks, vals[vals.length - 1]];
  const rows = edges.slice(0, -1).map((lo, i) => ({ label: `${fmt(lo)} – ${fmt(edges[i + 1])}`, color: colorAt(i) }));
  return { colorOf, rows };
}

export function fmtValue(v, metric) {
  if (v == null || !Number.isFinite(v)) return '—';
  // Small values (road PM2.5 at night, a tract's cases) keep two significant digits.
  if (v !== 0 && Math.abs(v) < 1) return v.toLocaleString('en-US', { maximumSignificantDigits: 2 });
  const d = Math.abs(v) >= 100 ? 0 : metric.digits ?? 1;
  return v.toLocaleString('en-US', { maximumFractionDigits: d });
}

// ---- charts --------------------------------------------------------------------

/** The citywide figure per hour: the sum for a total, else the mean over features. */
export function hourlyProfile(layer, metric) {
  return Array.from({ length: HOURS }, (_, h) => {
    let s = 0;
    let n = 0;
    layer.features.forEach((__, i) => {
      const v = layer.values(i, h);
      if (v != null && Number.isFinite(v)) { s += v; n += 1; }
    });
    const value = !n ? null : metric.sum ? s : s / n;
    return { hour: h, label: `${String(h).padStart(2, '0')}:00`, value };
  });
}

/** One feature's value per hour. */
export function featureProfile(layer, index) {
  return Array.from({ length: HOURS }, (_, h) => ({ hour: h, label: `${String(h).padStart(2, '0')}:00`, value: layer.values(index, h) }));
}

/** The `n` features with the highest value at hour `h`. `byName`: one entry per name, its
 *  highest feature (links: a road is several network links, e.g. each deck and span of the
 *  Bay Bridge, and would otherwise fill the list several times). */
export function topFeatures(layer, h, n = 10, { byName = false } = {}) {
  const ranked = layer.features
    .map((f, i) => ({ i, name: f.name, value: layer.values(i, h) }))
    .filter((x) => x.value != null && Number.isFinite(x.value))
    .sort((a, b) => b.value - a.value);
  if (!byName) return ranked.slice(0, n);
  const seen = new Set();
  const out = [];
  for (const x of ranked) {
    if (seen.has(x.name)) continue;
    seen.add(x.name);
    out.push(x);
    if (out.length === n) break;
  }
  return out;
}

/** A tract metric by household-income quintile of the tract: cases per 100k residents
 *  for a count, the population-weighted mean otherwise. */
export function byIncomeQuintile(layer, metric, h) {
  const acc = [1, 2, 3, 4, 5].map((q) => ({ q, value: 0, pop: 0 }));
  layer.features.forEach((f, i) => {
    const q = f.props?.income_quintile;
    const v = layer.values(i, h);
    const pop = f.props?.pop_total || 0;
    if (!q || v == null || !pop) return;
    const a = acc[q - 1];
    a.value += metric.sum ? v : v * pop;
    a.pop += pop;
  });
  return acc.map((a) => ({
    label: ['Lowest', 'Low', 'Middle', 'High', 'Highest'][a.q - 1],
    value: !a.pop ? null : metric.sum ? (1e5 * a.value) / a.pop : a.value / a.pop,
  }));
}
